"use client";

import { useMemo } from "react";
import { passwordChecks } from "@/lib/password-policy";

// Re-exported so the existing importers keep working; the rules themselves now
// live in @/lib/password-policy, shared with the server actions that enforce
// them.
export { firstPolicyError } from "@/lib/password-policy";

function Badge({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={[
          "inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none",
          ok
            ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-200"
            : "border-white/15 bg-white/5 text-neutral-400",
        ].join(" ")}
      >
        {ok ? "✓" : "•"}
      </span>
      <span className={ok ? "text-emerald-200" : "text-neutral-400"}>{children}</span>
    </div>
  );
}

export default function PasswordRequirements({ password }: { password: string }) {
  const checks = useMemo(() => passwordChecks(password), [password]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
        Password requirements
      </div>
      <div className="mt-2 space-y-1 text-sm">
        <Badge ok={checks.length}>At least 8 characters</Badge>
        <Badge ok={checks.upper}>At least 1 uppercase letter</Badge>
        <Badge ok={checks.number}>At least 1 number</Badge>
        <Badge ok={checks.special}>At least 1 special character</Badge>
      </div>
    </div>
  );
}
